import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';

import { ChartPointModel }           from '../typings/intelligence.chart-point';
import { ChartSeriesModel }          from '../typings/intelligence.chart-series';
import { ChartTooltipModel }         from '../typings/intelligence.chart-tooltip';
import { IntelligenceFormatService } from '../intelligence.format.service';

interface RenderedRow {
  bars : Array<{ color : string; percent : number; }>;
  label: string;
  point: ChartPointModel;
  value: string;
}

@Component({
  selector   : 'wor-intelligence-hbar-chart',
  template: require('./intelligence.hbar-chart.component.html')
})
export class HbarChartComponent implements OnChanges {
  @Input() format : string = 'money';
  @Input() points : Array<ChartPointModel> = [];
  @Input() series : Array<ChartSeriesModel> = [];

  @Output() rowPressed = new EventEmitter<ChartPointModel>();

  public hidden  : Array<string> = [];
  public rows    : Array<RenderedRow> = [];
  public tooltip : ChartTooltipModel = { rows: [], title: '', visible: false, x: 0, y: 0 };

  constructor (
    private intelligenceFormatService: IntelligenceFormatService
  ) {}

  ngOnChanges () : void {
    this.render();
  }

  formatValue ( value : number ) : string {
    return this.format === 'count'
      ? this.intelligenceFormatService.count(value)
      : this.intelligenceFormatService.money(value);
  }

  hideTooltip () : void {
    this.tooltip = { ...this.tooltip, visible: false };
  }

  isDrillable () : boolean {
    return this.rowPressed.observers.length > 0;
  }

  isHidden ( key : string ) : boolean {
    return this.hidden.indexOf(key) > -1;
  }

  onMove ( event : MouseEvent, row : RenderedRow ) : void {
    const wrapper = ( event.currentTarget as HTMLElement ).closest('.intelligence-chart-wrapper') as HTMLElement;
    const bounds = wrapper.getBoundingClientRect();

    this.tooltip = {
      rows: this.visibleSeries().map(item => ({
        color: item.color,
        label: item.label,
        value: this.formatValue(row.point.values[ item.key ] || 0)
      })),
      title  : row.label,
      visible: true,
      x      : Math.min(event.clientX - bounds.left + 14, bounds.width - 170),
      y      : Math.max(event.clientY - bounds.top - 10, 4)
    };
  }

  press ( row : RenderedRow ) : void {
    if ( this.isDrillable() ) {
      this.rowPressed.emit(row.point);
    }
  }

  toggleSeries ( key : string ) : void {
    if ( !this.isHidden(key) && this.visibleSeries().length < 2 ) {
      return;
    }

    this.hidden = this.isHidden(key)
      ? this.hidden.filter(item => item !== key)
      : this.hidden.concat(key);

    this.render();
  }

  visibleSeries () : Array<ChartSeriesModel> {
    return this.series.filter(item => !this.isHidden(item.key));
  }

  private render () : void {
    this.hideTooltip();
    this.rows = [];

    if ( !this.points.length || !this.visibleSeries().length ) {
      return;
    }

    let max = 0;

    this.points.forEach(point => {
      this.visibleSeries().forEach(item => max = Math.max(max, point.values[ item.key ] || 0));
    });

    if ( max <= 0 ) {
      max = 1;
    }

    this.rows = this.points.map(point => ({
      bars: this.visibleSeries().map(item => ({
        color  : item.color,
        percent: Math.max((( point.values[ item.key ] || 0 ) / max ) * 100, 0)
      })),
      label: point.label,
      point,
      value: this.formatValue(point.values[ this.visibleSeries()[ 0 ].key ] || 0)
    }));
  }
}
